// ════════════════════════════════════════════════════════════════════════════
//  ManDana — codes, and the pictures of them
//  ----------------------------------------------------------------------------
//  Every code this app hands out is a bearer credential with a short life: link
//  this device, ask a verifier for a way back in, sign in a screen that has no
//  camera. Typed, they are twenty characters somebody reads off one screen and
//  into another. Photographed, they are nothing to type at all.
//
//  THE PICTURE CARRIES A URL, NOT A CODE. `https://<origin>/?link=CODE`, which
//  means the phone's own camera app is a scanner for this app: it recognises a
//  link, offers to open it, and the app boots already holding the code. Nobody
//  installs anything, and there is no in-app camera in that path at all.
//
//  THE ORIGIN IS ALWAYS PASSED IN. Staging, a preview and the live site are
//  three origins with three different databases, and a QR that always said
//  mandana.app would send somebody testing on staging to production holding a
//  code production has never heard of.
//
//  A PICTURE IS NEVER THE ONLY WAY. Encoding can fail — no library, a string
//  longer than any QR version holds — and every one of those failures answers
//  null, because the code underneath can always be typed instead. Nothing here
//  ever throws on the boot path.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  /* TWO PARAMETERS, TWO OPPOSITE VERBS, and nothing here ever reads one as the
     other. `?link=` is a code being SPENT: the device holding it is buying a
     session with it. `?grant=` is a code being APPROVED: the device holding it
     already has a session and is being asked to hand one to the screen that
     showed the code. A grant code can never buy a session — only the long
     device secret can, and that never leaves the screen that is waiting. */
  const MD_LINK_PARAM = 'link';
  const MD_GRANT_PARAM = 'grant';

  function url(origin: unknown, param: string, code: unknown): string {
    if (typeof origin !== 'string' || !origin.trim()) return '';
    if (typeof code !== 'string' || !code.trim()) return '';
    return `${origin.replace(/\/+$/, '')}/?${param}=${encodeURIComponent(code.trim())}`;
  }

  /**
   * The code an address arrived with, or null.
   *
   * Null for anything that is not one, including an empty or blank value: this
   * is read on the boot path, before there is a surface to say anything on, and
   * a throw there would be a blank page rather than a front door.
   */
  function param(search: unknown, name: string): string | null {
    if (typeof search !== 'string' || !search) return null;
    try {
      const value = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(name);
      const code = typeof value === 'string' ? value.trim() : '';
      return code || null;
    } catch (_) { return null; }
  }

  /** `https://origin/?link=CODE` — empty when there is nothing to carry. */
  function mdLinkUrl(origin: unknown, code: unknown): string {
    return url(origin, MD_LINK_PARAM, code);
  }
  /** `https://origin/?grant=CODE` — the picture a waiting screen shows. */
  function mdGrantUrl(origin: unknown, code: unknown): string {
    return url(origin, MD_GRANT_PARAM, code);
  }
  function mdLandingLinkParam(search: unknown): string | null {
    return param(search, MD_LINK_PARAM);
  }
  function mdGrantParam(search: unknown): string | null {
    return param(search, MD_GRANT_PARAM);
  }

  /**
   * The code this page was opened with, taken out of the address as it is read.
   *
   * READING IT IS TAKING IT, and that is the whole design of this function.
   * The alternative is a rule — "strip before you redeem" — which is a rule
   * somebody eventually forgets, and forgetting it leaves a live credential in
   * the browser history, in a screenshot, and in whatever gets pasted when
   * somebody sends a friend "the page I'm on". There is no verb here that reads
   * the code and leaves it behind.
   *
   * Only the named parameter goes; anything else in the address — a QA flag, a
   * routed `#md=` hash — is somebody else's and is left exactly as it was.
   */
  function take(name: string): string | null {
    const code = param(g.location && g.location.search, name);
    if (!code) return null;
    try {
      if (g.history && typeof g.history.replaceState === 'function') {
        const here = new URL(g.location.href);
        here.searchParams.delete(name);
        g.history.replaceState(g.history.state, '', here.pathname + here.search + here.hash);
      }
    } catch (_) {
      /* A history that will not be rewritten is not a reason to refuse the
         code: the person is standing at a door holding it. The cost is a
         parameter left in the address, which is why this is a try and not a
         guard. */
    }
    return code;
  }

  function mdTakeLinkParam(): string | null { return take(MD_LINK_PARAM); }
  function mdTakeGrantParam(): string | null { return take(MD_GRANT_PARAM); }

  /**
   * The QR for a string, as a grid of dark/light modules — or null.
   *
   * Version 0 asks the library to pick the smallest version the data fits in,
   * and error correction M is the ordinary choice: it survives a fingerprint on
   * the screen without making the modules small enough to blur.
   */
  function mdQrModules(text: unknown): boolean[][] | null {
    if (typeof text !== 'string' || !text) return null;
    if (typeof g.qrcode !== 'function') return null;
    try {
      const qr = g.qrcode(0, 'M');
      qr.addData(text);
      qr.make();
      const n = qr.getModuleCount();
      if (!n) return null;
      const grid: boolean[][] = [];
      for (let r = 0; r < n; r += 1) {
        const row: boolean[] = [];
        for (let c = 0; c < n; c += 1) row.push(!!qr.isDark(r, c));
        grid.push(row);
      }
      return grid;
    } catch (_) {
      /* Too long for any version, or a library that changed shape. Either way
         the code is still a code and can still be typed. */
      return null;
    }
  }

  /**
   * One SVG path for a whole grid, in module coordinates — 0,0 to n,n.
   *
   * Runs of dark modules are merged along each row on purpose. A version-4 code
   * is over a thousand dark squares; drawn one rect each that is tens of
   * kilobytes of DOM for a picture 200px wide, and every one of them is a node
   * the browser lays out. Merged, it is one node.
   */
  function mdQrPath(grid: unknown): string {
    if (!Array.isArray(grid) || !grid.length) return '';
    let d = '';
    for (let r = 0; r < grid.length; r += 1) {
      const row = grid[r];
      if (!Array.isArray(row)) continue;
      let c = 0;
      while (c < row.length) {
        if (!row[c]) { c += 1; continue; }
        let run = 1;
        while (c + run < row.length && row[c + run]) run += 1;
        d += `M${c} ${r}h${run}v1h-${run}z`;
        c += run;
      }
    }
    return d;
  }

  const published = {
    mdLinkUrl, mdGrantUrl, mdLandingLinkParam, mdGrantParam,
    mdTakeLinkParam, mdTakeGrantParam, mdQrModules, mdQrPath,
  };
  Object.assign(g, published);

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = published;
})(typeof window !== 'undefined' ? window : globalThis);
